import React, { useState, useEffect } from "react";
import axios from "axios";

export default function SocialSettings() {
  const API = process.env.REACT_APP_API || "https://app.saravanabhava.in/";

  // ✅ Default structure
  const defaultSettings = {
    facebook: { enabled: false, pageId: "", token: "" },
    instagram: { enabled: false, igUserId: "", token: "" },
    twitter: { enabled: false, apiKey: "", apiSecret: "", accessToken: "", accessSecret: "" },
    linkedin: { enabled: false, token: "" },
  };

  const [settings, setSettings] = useState(defaultSettings);
  const [show, setShow] = useState({});
  const [loading, setLoading] = useState(true);

  // ✅ Load settings from DB
  useEffect(() => {
    axios
      .get(`${API}get-social-settings.php`)
      .then((res) => {
        if (res.data) {
          // Merge to avoid missing keys
          setSettings({ ...defaultSettings, ...res.data });
        }
      })
      .catch((err) => console.error("Load error:", err))
      .finally(() => setLoading(false));
  }, [API]);

  // ✅ Save per platform
  const handleSave = async (platform) => {
    try {
      await axios.post(`${API}save-social-settings.php`, {
        platform,
        data: settings[platform],
      });
      alert(`${platform} settings saved successfully!`);
    } catch (err) {
      console.error(err);
      alert("Save failed!");
    }
  };

  // ✅ Test API credentials
  const handleTest = async (platform) => {
    try {
      const res = await axios.post(`${API}test-${platform}.php`, settings[platform]);
      alert(`${platform} test: ${res.data.status}`);
    } catch (err) {
      console.error(err);
      alert("Test failed!");
    }
  };

  const toggleShow = (field) => {
    setShow((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  if (loading) return <p>Loading...</p>;

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-4">Social Media Configuration</h2>
      <p className="text-gray-600 mb-6">
        Enable platforms and paste the required credentials. Values are stored securely.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Facebook */}
        <Card
          title="Facebook Page"
          platform="facebook"
          enabled={settings.facebook.enabled}
          onEnable={(val) =>
            setSettings({ ...settings, facebook: { ...settings.facebook, enabled: val } })
          }
        >
          <Input
            label="Page ID"
            value={settings.facebook.pageId}
            onChange={(e) =>
              setSettings({ ...settings, facebook: { ...settings.facebook, pageId: e.target.value } })
            }
          />
          <PasswordInput
            label="Page Access Token"
            value={settings.facebook.token}
            show={show.facebookToken}
            toggle={() => toggleShow("facebookToken")}
            onChange={(e) =>
              setSettings({ ...settings, facebook: { ...settings.facebook, token: e.target.value } })
            }
          />
          <Buttons onSave={() => handleSave("facebook")} onTest={() => handleTest("facebook")} />
        </Card>

        {/* Instagram */}
        <Card
          title="Instagram (Business)"
          platform="instagram"
          enabled={settings.instagram.enabled}
          onEnable={(val) =>
            setSettings({ ...settings, instagram: { ...settings.instagram, enabled: val } })
          }
        >
          <Input
            label="IG User ID"
            value={settings.instagram.igUserId}
            onChange={(e) =>
              setSettings({
                ...settings,
                instagram: { ...settings.instagram, igUserId: e.target.value },
              })
            }
          />
          <PasswordInput
            label="IG Access Token"
            value={settings.instagram.token}
            show={show.instagramToken}
            toggle={() => toggleShow("instagramToken")}
            onChange={(e) =>
              setSettings({
                ...settings,
                instagram: { ...settings.instagram, token: e.target.value },
              })
            }
          />
          <Buttons onSave={() => handleSave("instagram")} onTest={() => handleTest("instagram")} />
        </Card>

        {/* Twitter */}
        <Card
          title="Twitter / X"
          platform="twitter"
          enabled={settings.twitter.enabled}
          onEnable={(val) =>
            setSettings({ ...settings, twitter: { ...settings.twitter, enabled: val } })
          }
        >
          {["apiKey", "apiSecret", "accessToken", "accessSecret"].map((field) => (
            <PasswordInput
              key={field}
              label={field}
              value={settings.twitter[field]}
              show={show[`twitter_${field}`]}
              toggle={() => toggleShow(`twitter_${field}`)}
              onChange={(e) =>
                setSettings({ ...settings, twitter: { ...settings.twitter, [field]: e.target.value } })
              }
            />
          ))}
          <Buttons onSave={() => handleSave("twitter")} onTest={() => handleTest("twitter")} />
        </Card>

        {/* LinkedIn */}
        <Card
          title="LinkedIn"
          platform="linkedin"
          enabled={settings.linkedin.enabled}
          onEnable={(val) =>
            setSettings({ ...settings, linkedin: { ...settings.linkedin, enabled: val } })
          }
        >
          <PasswordInput
            label="Access Token"
            value={settings.linkedin.token}
            show={show.linkedinToken}
            toggle={() => toggleShow("linkedinToken")}
            onChange={(e) =>
              setSettings({ ...settings, linkedin: { ...settings.linkedin, token: e.target.value } })
            }
          />
          <Buttons onSave={() => handleSave("linkedin")} onTest={() => handleTest("linkedin")} />
        </Card>
      </div>
    </div>
  );
}

// 🔹 Reusable Components
const Card = ({ title, platform, enabled, onEnable, children }) => (
  <div className="border rounded-xl p-4 shadow-sm">
    <div className="flex justify-between items-center mb-2">
      <h3 className="font-semibold">{title}</h3>
      <input
        type="checkbox"
        checked={enabled}
        onChange={(e) => onEnable(e.target.checked)}
      />
    </div>
    {children}
  </div>
);

const Input = ({ label, value, onChange }) => (
  <input
    type="text"
    placeholder={label}
    className="w-full p-2 border rounded mb-2"
    value={value}
    onChange={onChange}
  />
);

const PasswordInput = ({ label, value, show, toggle, onChange }) => (
  <div className="flex items-center mb-2">
    <input
      type={show ? "text" : "password"}
      placeholder={label}
      className="flex-1 p-2 border rounded"
      value={value}
      onChange={onChange}
    />
    <button
      type="button"
      className="ml-2 text-sm text-blue-600"
      onClick={toggle}
    >
      {show ? "Hide" : "Show"}
    </button>
  </div>
);

const Buttons = ({ onSave, onTest }) => (
  <div className="flex space-x-2">
    <button onClick={onSave} className="bg-blue-600 text-white px-3 py-1 rounded">
      Save
    </button>
    <button onClick={onTest} className="bg-gray-600 text-white px-3 py-1 rounded">
      Test
    </button>
  </div>
);
